import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, Input, Modal } from '../../../../../common/_custom';
import styles from './AddToWatchlist.module.scss';
import { addCompanyToWatchlist } from '../../../../../store/investor/investorApiHelper';
import { IDENTIFIER, WEBSITE, WEBSITE_ERROR_MSG } from '../../../../../utils/constants/companyLogin';
import { Mixpanel } from '../../../../../utils/mixpanel';
import { INVESTOR_ADD_TO_WATCHLIST_CLICK } from '../../../../../utils/constants/mixpanelEvents/investorEvents';
import { infoPurpleIcon, plusPurpleCircle } from '../../../../../assets/hostedassets';
import useSearchResultFetcher from '../../../../../common/GlobalSearch/useSearchResultFetcher';
import { AddToWatchlistPrefill } from './prefillBuilder';
import { isValidWebsite, buildWebsiteUrl } from './components/WebsitePreview/websiteShortcut';
import { useWebsitePreview } from './components/WebsitePreview/useWebsitePreview';
import WebsitePreview from './components/WebsitePreview/WebsitePreview';
import {
    extractIdentifier,
    resolveAddLeadIdentifier,
    resolveAddLeadIdentifierError,
    resolveAddLeadProbeError,
    resolveEntityPickIntent,
} from './identifierUtils';
import AddToWatchlistDropdown from './AddToWatchlistDropdown';
import { GLOBAL_SEARCH_GROUPS } from '../../../../../common/MagicUpload/Utils/MagicUploadConstants';
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks';
import { useConfigTableModalGate } from '../../../../../common/ConfigTableModal/ConfigTableModalGate';
import { useVisibilityConfig } from '../../../../../utils/useVisibilityConfig';
import { setConfigTableModal, updateCompany } from '../../../../../store/investor/action';
import { ENTITY_TYPES } from '../../../../../store/investor/constants';
import { getPlatformUserId } from '../../../../../utils/utils';
import { buildTriggerKeys, evaluateAgainstMap } from '../../../../../common/ConfigTableModal/visibilityRules';
import { AddCompanyToWatchlistPayload } from '../../../../../store/investor/service';
import { toast } from 'react-toastify';

export enum ADD_JOURNEY_V2 {
    ADD_TO_SCAN = 'ADD_TO_SCAN',
    ADD_TO_WATCH = 'ADD_TO_WATCH',
}

// Distinguishes "probe failed and addCompanyToWatchlist's ERROR branch already toasted it"
// (duplicate lead) from a probe failure that still needs an identifier-field error set.
const PROBE_ALREADY_SURFACED = Symbol('probe-already-surfaced');
type ProbeResult = string | typeof PROBE_ALREADY_SURFACED | null;

// The subset of the lead payload collected via the deferred owner-collection modal.
type CollectedLeadFields = Pick<AddCompanyToWatchlistPayload, 'owners' | 'customFields'>;

// Identity + collected fields for a lead whose create succeeded but whose owners/customFields
// patch failed — enough for a later retry to patch without re-creating (see createdLeadRef).
type PendingLeadPatch = {
    assesseeOrgId: string | undefined;
    orgName?: string;
    collected?: CollectedLeadFields;
};

function AddToWatchlistV2({
    prefill,
    modalOpen,
    closeModal,
    investorOrgId,
    onSuccess = () => {},
    handleAlreadyAddedInPlace = false,
    source,
}: {
    prefill?: AddToWatchlistPrefill;
    modalOpen?: boolean;
    closeModal: any;
    investorOrgId: string;
    onSuccess?: (data?: any) => void;
    handleAlreadyAddedInPlace?: boolean;
    source?: string;
}) {
    const location = useLocation();
    const userEmail = useAppSelector((state) => (state.user as any)?.userData?.emailId || (state.user as any)?.userData?.email);
    const userId = useAppSelector((state) => (state.user as any)?.userData?.userId);
    const userName = useAppSelector((state) => (state.user as any)?.userData?.userName);
    const isProspectsPage = location.pathname?.toLowerCase?.().includes('/watch');
    const journey = isProspectsPage ? ADD_JOURNEY_V2.ADD_TO_WATCH : ADD_JOURNEY_V2.ADD_TO_SCAN;
    const IDENTIFY_ERROR_MSG = "Entity couldn't be identified. Please try again with a different Legal name.";
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        [WEBSITE]: '',
        [IDENTIFIER]: '',
        brandName: '',
    });
    const [formErrors, setFormErrors] = useState({
        [WEBSITE]: '',
        [IDENTIFIER]: '',
        brandName: '',
    });
    const inviteCode = sessionStorage.getItem('invite_code') ?? localStorage.getItem('invite_code') ?? '';
    const hasError = Object.values(formErrors).some((e) => !!e);

    const {
        searchText,
        setSearchText,
        searchResults,
        searchLoading,
        setSearchResults,
        getTextForKey,
        fetchCompanyInfoForResult,
        navigateUser,
    } = useSearchResultFetcher();
    const [showGlobalDropdown, setShowGlobalDropdown] = useState(false);
    const [infoLoading, setInfoLoading] = useState({ website: false, brand: false });
    const [selectedAddNewResult, setSelectedAddNewResult] = useState<any>(null);
    const [prefillLoading, setPrefillLoading] = useState<{ identifier: boolean; website: boolean; brand: boolean }>({
        identifier: !!prefill?.loading?.identifier,
        website: !!prefill?.loading?.website,
        brand: !!prefill?.loading?.brand,
    });
    const payloadIdentifierRef = useRef<string>('');
    const isAddUsingFlowRef = useRef<boolean>(false);
    // Tracks whether createLeadThenApplyFields has started, so the deferred modal's onCloseCallback
    // (which fires on the Proceed path too, mid-POST) only clears loading on a real cancel/X.
    const submitStartedRef = useRef<boolean>(false);
    // Set once create succeeds but the follow-up owners/customFields patch fails, so the next
    // Confirm click retries only the patch instead of re-creating the lead (see addToWatchlist).
    const createdLeadRef = useRef<PendingLeadPatch | null>(null);
    const dispatch = useAppDispatch();
    const ownerDropdownOptions = useAppSelector((state) => state.investor.ownerDropdownOptions);
    const { isConfigLoaded, getFeatureVisibility } = useVisibilityConfig();
    // Third conjunct mirrors InvestorPrivateRoutes' resolveShouldShow — the route gate that
    // decides whether the collect modal actually renders — by running the SAME
    // buildTriggerKeys/evaluateAgainstMap precedence engine for this call site's fixed trigger
    // (entity 'Lead', actionType 'create', no dropdownOption/triggers below), so the two can't
    // independently drift again.
    const { specificKeys, genericKeys } = buildTriggerKeys(
        ENTITY_TYPES.LEAD,
        [{ actionType: 'create', values: [] }],
        'showUserCreationModal',
    );
    const leadCreateVisibilityMap = Object.fromEntries(
        [...specificKeys, ...genericKeys].map((key) => [key, { write: getFeatureVisibility(key).write === true }]),
    );
    const hasGate =
        useConfigTableModalGate() &&
        isConfigLoaded &&
        evaluateAgainstMap(leadCreateVisibilityMap, specificKeys, genericKeys, (entry) => entry.write === true).visible;

    useEffect(() => {
        if (!modalOpen) return;
        const resolvedSource = source || prefill?.source || 'unknown';
        Mixpanel.track('lead_add_modal_opened', {
            source: resolvedSource,
            userEmail: userEmail || '',
            current_url: window.location.href,
        });
    }, [modalOpen, prefill?.source, source, userEmail]);

    useEffect(() => {
        if (!modalOpen) return;
        const resolved = {
            displayIdentifier: prefill?.identifierDisplay || '',
            payloadIdentifier: prefill?.identifierPayload || '',
            websiteValue: prefill?.website || '',
            brandValue: prefill?.brand || '',
            prefillLoading: prefill?.loading || { identifier: false, website: false, brand: false },
        };
        isAddUsingFlowRef.current = !!prefill?.isAddUsingSearchText;
        payloadIdentifierRef.current = resolved.payloadIdentifier;
        // A fresh open (new company, or reopened after a cancel) must not retry a stale
        // pending patch left over from a previous, unrelated Confirm attempt.
        createdLeadRef.current = null;
        setFormData({
            [WEBSITE]: resolved.websiteValue,
            [IDENTIFIER]: resolved.displayIdentifier,
            brandName: resolved.brandValue,
        });
        setFormErrors({ [WEBSITE]: '', [IDENTIFIER]: '', brandName: '' });
        const openDropdown = !!prefill?.shouldOpenDropdown;
        // searchText drives the global-search fetch, so seed it only when the dropdown will show the results.
        setSearchText(openDropdown ? resolved.displayIdentifier : '');
        setShowGlobalDropdown(openDropdown && resolved.displayIdentifier.length >= 3);
        const normalizedLoading = {
            identifier: !!resolved.prefillLoading?.identifier,
            website: !!resolved.prefillLoading?.website,
            brand: !!resolved.prefillLoading?.brand,
        };
        setPrefillLoading(normalizedLoading);
        setSelectedAddNewResult(resolved.displayIdentifier ? prefill?.baseResult || null : null);
    }, [modalOpen, prefill]);

    const closeWithReason = (reason: string) => {
        // A pending retry must not survive the modal closing (cancel/X included) — the next
        // open (possibly for a different company) starts clean.
        createdLeadRef.current = null;
        if (typeof closeModal === 'function') {
            closeModal(false);
        }
    };

    const changeFormValue = (value, key) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
    };

    // The redirect action is offered only for a valid full website (see websiteShortcut).
    const websiteValue = (formData[WEBSITE] || '').trim();
    const isWebsiteFetching = infoLoading.website || prefillLoading.website;
    const websiteIsValid = isValidWebsite(websiteValue);

    const openWebsite = () => {
        if (!websiteIsValid) return;
        window.open(buildWebsiteUrl(websiteValue), '_blank', 'noopener,noreferrer');
    };

    // Hero-image preview for whatever website is currently resolved (picked,
    // auto-fetched, or typed). Debounced and aborted inside the hook.
    const websitePreview = useWebsitePreview(websiteValue, isWebsiteFetching);
    // The field's single display error: a real validation error wins, else the
    // preview lookup's advisory "not found". Kept OUT of formErrors on purpose —
    // formErrors feeds hasError → isSubmitDisabled, and this advisory heuristic
    // on an optional field must never disable Confirm.
    const websiteFieldError = formErrors[WEBSITE] || (websitePreview.notFound ? "We couldn't find this website" : '');

    /**
     * Handles a selection from global search:
     * - Normalizes org name/website into form fields and clears validation errors.
     * - Preserves search text for follow-up queries and hides dropdown/UI chrome.
     * - Business logic: routes "already added" selections to either inline success or navigation;
     *   for "add new" selections it clears stale autofill, shows loading, fetches company info,
     *   and rehydrates identifier/brand fields from the fetched payload.
     */
    const handleGlobalSelect = async (result, groupKey?: string) => {
        const orgName = result?.orgName || result?.orgDisplayName || '';
        const websiteUrl = result?.websiteUrl || '';
        const identifierValue = extractIdentifier(result, orgName);
        payloadIdentifierRef.current = identifierValue;
        // Picking a real entity means we're no longer "adding by typed text", so reset the mode
        // flag. addToWatchlist then resolves to payloadIdentifierRef (the entity's CIN/PAN/GSTIN)
        // instead of the typed display name. Symmetric with typing, which clears the picked state.
        isAddUsingFlowRef.current = false;
        changeFormValue(orgName, IDENTIFIER); // keep UI showing name
        changeFormValue(websiteUrl, WEBSITE);
        setFormErrors((errors) => ({ ...errors, [IDENTIFIER]: '', [WEBSITE]: '' }));
        setShowGlobalDropdown(false);
        setSearchResults(null);
        // Keep user-entered search text for follow-up calls; avoid toggling dropdown reopen loop.
        setSearchText(orgName);
        const isAddNew = groupKey === GLOBAL_SEARCH_GROUPS.ADD_NEW;
        setSelectedAddNewResult(isAddNew ? result : null);
        if (groupKey === GLOBAL_SEARCH_GROUPS.ALREADY_ADDED && result?.orgId) {
            if (handleAlreadyAddedInPlace) {
                onSuccess?.({
                    orgName: orgName,
                    assesseeOrgId: result.orgId,
                });
                closeWithReason('alreadyAdded-inplace');
            } else {
                navigateUser(result.orgId, result?.companyAccessType || '');
                closeWithReason('alreadyAdded-navigate');
            }
            return;
        }
        if (isAddNew) {
            // Clear old autofill values before fetching new info
            changeFormValue('', WEBSITE);
            changeFormValue('', 'brandName');
            setInfoLoading({ website: true, brand: true });
            try {
                const companyInfo = await fetchCompanyInfoForResult(result, orgName);
                // Recompute payload identifier when company info arrives (prefer CIN/PAN/legal_name)
                const updatedPayloadIdentifier = extractIdentifier(result, orgName);
                payloadIdentifierRef.current = updatedPayloadIdentifier;
                changeFormValue(companyInfo?.website || '', WEBSITE);
                // Do NOT use legal_name for brand; only brand_name if present.
                changeFormValue(companyInfo?.brand_name || '', 'brandName');
            } finally {
                setInfoLoading({ website: false, brand: false });
            }
        }
    };

    const hasFieldLoader =
        infoLoading.website ||
        infoLoading.brand ||
        searchLoading ||
        prefillLoading.identifier ||
        prefillLoading.website ||
        prefillLoading.brand;
    const isSubmitDisabled = loading || hasFieldLoader || hasError || !investorOrgId || !formData[IDENTIFIER]?.trim();
    const submitButtonText = 'Confirm';
    const isIdentifierPrefillLoading = prefillLoading.identifier;

    /**
     * Builds the backend payload from component state, run at Confirm time:
     * - Validates website format and determines the identifier payload based on flow
     *   (direct user input vs. prefill/legal name fallback).
     * - Returns null (after setting the field error) when website validation fails.
     * Everything resolved here is captured into the returned object so a later deferred
     * submit (after the owner-collection round trip) cannot see stale/reset state.
     */
    const buildPayload = (): AddCompanyToWatchlistPayload | null => {
        if (formData[WEBSITE] && !formData[WEBSITE]?.includes('.')) {
            setFormErrors((errors) => ({ ...errors, [WEBSITE]: WEBSITE_ERROR_MSG }));
            return null;
        }
        const legalNameFromPrefill =
            prefill?.companyInfo?.legal_name ||
            prefill?.companyInfo?.data?.legal_name ||
            prefill?.baseResult?.companyInfo?.legal_name ||
            prefill?.baseResult?.companyInfo?.data?.legal_name;
        // isAddUsingFlowRef is true only for a genuine add-by-typed-text flow. Picking a real entity
        // (global bar or modal dropdown) resets it to false in handleGlobalSelect, so a pick resolves
        // to payloadIdentifierRef — the entity's CIN/PAN/GSTIN — rather than the typed display name.
        const identifierForPayload = resolveAddLeadIdentifier({
            isAddUsingFlow: isAddUsingFlowRef.current,
            displayName: formData[IDENTIFIER],
            payloadIdentifier: payloadIdentifierRef.current,
            legalNameFromPrefill,
        });
        const entityPickIntent = resolveEntityPickIntent(selectedAddNewResult);
        return {
            ...(formData[WEBSITE] && { websiteUrl: formData[WEBSITE] }),
            investorOrgId: investorOrgId,
            journeyName: journey,
            ...(identifierForPayload && { identifier: identifierForPayload }),
            ...(formData.brandName && { brandName: formData.brandName }),
            ...(inviteCode && { inviteCode }),
            ...(selectedAddNewResult && { searchResultContext: selectedAddNewResult }),
            ...(entityPickIntent.addAsIndividualPan && { addAsIndividualPan: entityPickIntent.addAsIndividualPan }),
            ...(entityPickIntent.selectedEntityId && { selectedEntityId: entityPickIntent.selectedEntityId }),
        };
    };

    /**
     * Converts the deferred-collection owners map (ownerType -> userId) into the OwnerType[]
     * shape updateOrgMaster expects, mirroring useConfigTableModal's createPayload/saveToLead.
     */
    const buildOwnersForUpdate = (owners: Record<string, string>) =>
        Object.entries(owners).map(([ownerType, ownerId]) => {
            const owner = ownerDropdownOptions.find((option) => option.userId === ownerId);
            return {
                name: owner?.customSortName || owner?.name || '',
                userId: ownerId,
                activityBy: userName,
                activityDate: new Date().toISOString(),
                ownerType,
            };
        });

    /**
     * Patches the just-created lead with the owners/customFields collected via the deferred
     * modal, via the same updateCompany API ConfigTableModal's own saveToLead uses — including
     * its convention of toasting the failure itself. The lead already exists by the time this
     * runs, so the caller must await it rather than reporting success on a lead that silently
     * ended up ownerless.
     */
    const updateLeadFields = (orgId: string, collected: CollectedLeadFields): Promise<boolean> =>
        new Promise((resolve) => {
            dispatch(
                updateCompany(
                    {
                        orgId,
                        ...(collected.owners && { owners: buildOwnersForUpdate(collected.owners) }),
                        ...(collected.customFields && { customFields: collected.customFields }),
                    },
                    {
                        onSuccess: () => resolve(true),
                        onError: () => {
                            toast.error('Lead was created, but its details could not be saved. Press Confirm again to retry saving them.');
                            resolve(false);
                        },
                    },
                ),
            );
        });

    /**
     * Patches the just-created lead's collected owners/customFields onto it, then runs the
     * shared success path (reset form, propagate lead data via onSuccess, close modal, release
     * loading). If the patch fails, the lead itself still exists: createdLeadRef remembers its
     * identity and the collected fields so the next Confirm click (see addToWatchlist) retries
     * only this patch instead of re-creating the lead.
     */
    const applyCollectedFieldsThenFinish = async ({ assesseeOrgId, orgName, collected }: PendingLeadPatch) => {
        if (assesseeOrgId && (collected?.owners || collected?.customFields)) {
            const patched = await updateLeadFields(assesseeOrgId, collected);
            if (!patched) {
                createdLeadRef.current = { assesseeOrgId, orgName, collected };
                setLoading(false);
                return;
            }
        }
        createdLeadRef.current = null;
        setFormData({ [WEBSITE]: '', [IDENTIFIER]: '', brandName: '' });
        const leadDataPayload = assesseeOrgId ? { orgName, assesseeOrgId } : null;
        onSuccess?.(leadDataPayload);
        closeWithReason('submit-success');
        setLoading(false);
    };

    /**
     * Sends the built payload to the backend to create the lead — owners/customFields are no
     * longer sent here (the backend stamping this call used to rely on is being removed) — then,
     * as a second step, patches the collected owners/customFields onto the newly created lead via
     * applyCollectedFieldsThenFinish. Surfaces identifier errors and toggles loading on failure.
     */
    const createLeadThenApplyFields = async (payload: AddCompanyToWatchlistPayload, collected?: CollectedLeadFields) => {
        submitStartedRef.current = true;
        await addCompanyToWatchlist(payload, {
            onSuccess: async (data) => {
                const identifierError = resolveAddLeadIdentifierError(data, IDENTIFY_ERROR_MSG);
                if (identifierError) {
                    setFormErrors((errors) => ({ ...errors, [IDENTIFIER]: identifierError }));
                    setLoading(false);
                    return;
                }
                await applyCollectedFieldsThenFinish({
                    assesseeOrgId: data?.orgInfo?.assesseeOrgId,
                    orgName: data?.orgInfo?.orgName || formData[IDENTIFIER],
                    collected,
                });
            },
            onError: () => {
                setLoading(false);
            },
        });
    };

    /**
     * Pre-flight READ-mode check before the collect-fields modal opens, so a company that can't
     * be identified is caught at Confirm instead of after five fields. Reuses addCompanyToWatchlist
     * verbatim — the only difference from the real submit is `mode: 'READ'`. Resolves to an
     * identifier-field error, the PROBE_ALREADY_SURFACED sentinel when addCompanyToWatchlist's own
     * ERROR branch already toasted (duplicate lead), or null when the entity resolved.
     */
    const probeEntity = async (payload: AddCompanyToWatchlistPayload): Promise<ProbeResult> => {
        let result: ProbeResult = null;
        await addCompanyToWatchlist(
            { ...payload, mode: 'READ' },
            {
                onSuccess: (data) => {
                    result = resolveAddLeadProbeError(data, IDENTIFY_ERROR_MSG);
                },
                onError: () => {
                    result = PROBE_ALREADY_SURFACED;
                },
            },
        );
        return result;
    };

    /**
     * Confirm handler: if a previous attempt created the lead but failed to patch its collected
     * fields, retry only that patch (createdLeadRef) — never re-create the lead. Otherwise builds
     * the payload, then either submits directly (gate off, or the visibility config hasn't loaded
     * yet — fail open) or probes the entity (unless an inviteCode is present — see shouldProbe)
     * and defers to the owner-collection modal, submitting once it resolves.
     */
    const addToWatchlist = async () => {
        Mixpanel.track(INVESTOR_ADD_TO_WATCHLIST_CLICK);

        if (createdLeadRef.current) {
            setLoading(true);
            return applyCollectedFieldsThenFinish(createdLeadRef.current);
        }

        const payload = buildPayload();
        if (!payload) return;

        if (!hasGate) {
            setLoading(true);
            return createLeadThenApplyFields(payload);
        }

        setLoading(true);
        submitStartedRef.current = false;
        const shouldProbe = !payload.inviteCode;
        if (shouldProbe) {
            const probeError = await probeEntity(payload);
            if (probeError !== null) {
                if (probeError !== PROBE_ALREADY_SURFACED) {
                    setFormErrors((errors) => ({ ...errors, [IDENTIFIER]: probeError }));
                }
                setLoading(false);
                return;
            }
        }
        const platformUserId = getPlatformUserId() || userId;
        // Only prefill the adder as lead owner when they're actually a selectable option —
        // the backend only accepts an owner belonging to the request's PNO with userType
        // INVESTOR/ADMIN_TEAM (the same set ownerDropdownOptions is built from). Seeding an
        // adder outside that set would pass FE's non-emptiness check but get silently dropped
        // server-side, creating an ownerless lead. Leave it unset so the field is picked explicitly.
        const isAdderSelectableOwner = (ownerDropdownOptions || []).some((option) => option.userId === platformUserId);
        const defaultSeed = isAdderSelectableOwner ? { owners: { leadOwner: platformUserId } } : undefined;
        dispatch(
            setConfigTableModal({
                show: true,
                data: {
                    entity: ENTITY_TYPES.LEAD,
                    actionType: 'create',
                    deferPersistence: true,
                    rowData: defaultSeed,
                    onSuccess: (collectedFormData) => {
                        const collected = collectedFormData?.[ENTITY_TYPES.LEAD];
                        return createLeadThenApplyFields(payload, collected);
                    },
                    onCloseCallback: () => {
                        // Fires on Proceed too (mid-POST) — only clear loading on a real cancel/X.
                        if (!submitStartedRef.current) setLoading(false);
                    },
                },
            }),
        );
    };

    return (
        <Modal open={modalOpen ?? true} onClose={() => closeWithReason('modal-onClose')} size="flexible" persistent>
            <div className={styles.AddToWatchlist}>
                <div className={styles.Title}>Add Lead</div>
                {!investorOrgId && (
                    <div className={styles.IdentifierFound} style={{ color: '#df666a', marginTop: '12px' }}>
                        <img src={infoPurpleIcon} alt="" height={'10px'} style={{ filter: 'hue-rotate(180deg)' }} />
                        Organization ID is missing. Please refresh the page.
                    </div>
                )}

                <div style={{ position: 'relative', marginTop: '24px' }} className={styles.FieldWithLoader}>
                    <Input
                        placeholder={'Name, CIN, LLPIN, PAN or GSTIN (min 3 char)'}
                        value={isIdentifierPrefillLoading ? 'Fetching...' : formData[IDENTIFIER]}
                        onChange={(val) => {
                            setFormErrors((errors) => ({ ...errors, [WEBSITE]: '', [IDENTIFIER]: '' }));
                            changeFormValue(val, IDENTIFIER);
                            payloadIdentifierRef.current = '';
                            setSearchText(val);
                            setShowGlobalDropdown(true);
                            setSelectedAddNewResult(null);
                        }}
                        errorMessage={formErrors[IDENTIFIER]}
                        isValid={!formErrors[IDENTIFIER]}
                        disabled={loading}
                        inputStyle={{ fontWeight: '550' }}
                        allowAllCharacters
                    />
                    {(searchLoading && showGlobalDropdown) || prefillLoading.identifier ? (
                        <span className={styles.FieldLoaderOverlay} aria-label="Searching" role="status">
                            <span className={styles.FieldLoader} />
                        </span>
                    ) : null}
                    <AddToWatchlistDropdown
                        visible={showGlobalDropdown}
                        searchText={searchText}
                        searchResults={searchResults}
                        onSelectAlreadyAdded={(result) => handleGlobalSelect(result, GLOBAL_SEARCH_GROUPS.ALREADY_ADDED)}
                        onSelectAddNew={(result) => handleGlobalSelect(result, GLOBAL_SEARCH_GROUPS.ADD_NEW)}
                        onFooterAddNew={() => {
                            changeFormValue(searchText, IDENTIFIER);
                            setFormErrors((errors) => ({ ...errors, [IDENTIFIER]: '', [WEBSITE]: '' }));
                            setShowGlobalDropdown(false);
                            setSearchResults(null);
                            setSelectedAddNewResult(null);
                        }}
                        footerLabel={`Add new organisation "${searchText}"`}
                        useWideLayout
                        wrapperClassName={styles.GlobalDropdownWrapper}
                        wrapperStyle={{
                            top: 'calc(100% + 0.5rem)',
                            maxHeight: '18rem',
                            boxShadow: '0px 4px 12px rgba(0,0,0,0.12)',
                            zIndex: 5,
                        }}
                    />
                </div>

                <div className={styles.FieldWithLoader}>
                    {/* Inner wrapper so the absolutely-positioned loader centres on the
                        input alone — the preview below would otherwise make its 50%
                        anchor include the image. */}
                    <div className={styles.WebsiteInputRow}>
                        <Input
                            placeholder="Website (Optional)"
                            value={infoLoading.website || prefillLoading.website ? 'Fetching...' : formData[WEBSITE]}
                            onChange={(val) => {
                                setFormErrors((errors) => ({ ...errors, [WEBSITE]: '', [IDENTIFIER]: '' }));
                                changeFormValue(val, WEBSITE);
                            }}
                            style={{ marginTop: '16px' }}
                            disabled={loading || infoLoading.website}
                            readOnly={infoLoading.website}
                            isValid={!websiteFieldError}
                            errorMessage={websiteFieldError}
                            allowAllCharacters={true}
                        />
                        {infoLoading.website || prefillLoading.website ? (
                            <span className={styles.FieldLoaderOverlay} aria-label="Loading website" role="status">
                                <span className={styles.FieldLoader} />
                            </span>
                        ) : null}
                    </div>
                    <WebsitePreview
                        companyName={formData[IDENTIFIER]}
                        preview={websitePreview}
                        onOpenWebsite={websiteIsValid ? openWebsite : undefined}
                    />
                </div>

                <div className={`${styles.FieldWithLoader} ${styles.LastField}`}>
                    <Input
                        placeholder="Brand Name (Optional)"
                        value={infoLoading.brand || prefillLoading.brand ? 'Fetching...' : formData.brandName}
                        onChange={(val) => {
                            setFormErrors((errors) => ({ ...errors, brandName: '' }));
                            changeFormValue(val, 'brandName');
                        }}
                        style={{ marginTop: '16px' }}
                        disabled={loading || infoLoading.brand}
                        readOnly={infoLoading.brand}
                        isValid={!formErrors.brandName}
                        errorMessage={formErrors.brandName}
                        allowAllCharacters={true}
                    />
                    {infoLoading.brand || prefillLoading.brand ? (
                        <span className={styles.FieldLoaderOverlay} aria-label="Loading brand name" role="status">
                            <span className={styles.FieldLoader} />
                        </span>
                    ) : null}
                </div>

                <Button
                    disabled={isSubmitDisabled}
                    variant="primary"
                    onClick={addToWatchlist}
                    text={submitButtonText}
                    style={{ width: '100%' }}
                    loading={loading}
                />
            </div>
        </Modal>
    );
}

export default AddToWatchlistV2;
